import {
  ArrowUpFromLine,
  Download,
  FolderOpen,
  RefreshCw,
} from 'lucide-react'
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import {
  OpenDestinationIcons,
  resolveOpenExternalIcon,
} from '@/lib/open-destination-icons'
import {
  driveEffectiveMime,
  isDriveFileImportable,
} from '@/lib/drive-importability'
import type {
  SidebarTreeBreadcrumb,
  SidebarTreeNode,
  SidebarTreeSection,
} from '@/components/layout/sidebar/sidebar-tree.types'
import { sidebarTreeIconForMime } from '@/components/layout/sidebar/sidebar-tree-icons'
import { formatRelativeTime } from '@/lib/format-relative-time'

export type DriveShortcutTarget = { id: string; mimeType: string }

export type DriveFileMeta = {
  fileId: string
  mimeType: string
  webViewLink?: string
  isFolder: boolean
  isShortcut?: boolean
  ownedByMe?: boolean
  shared?: boolean
  /** Present when isShortcut=true. Use these for preview/import. */
  shortcutTarget?: DriveShortcutTarget | null
}

export type DriveBrowseItem = {
  id: string
  name: string
  mimeType: string
  modifiedTime?: string
  webViewLink?: string
  isFolder?: boolean
  isShortcut?: boolean
  ownedByMe?: boolean
  shared?: boolean
  shortcutTarget?: DriveShortcutTarget | null
}

export type DriveCorpus = 'myDrive' | 'drive' | 'sharedWithMe' | 'recent'

// Represents a single shared drive listed from GET /drives
export type SharedDriveItem = { id: string; name: string }

/**
 * Maps a list of shared drives to SidebarTreeNode folder nodes.
 * Clicking a drive navigates into it (handled by the caller).
 */
export function mapDrivesToFolderNodes(
  drives: SharedDriveItem[],
): SidebarTreeNode<DriveFileMeta>[] {
  return drives.map((drive) => ({
    id: drive.id,
    kind: 'folder' as const,
    label: drive.name,
    icon: 'folder' as const,
    meta: {
      fileId: drive.id,
      mimeType: 'application/vnd.google-apps.drive',
      isFolder: true,
    },
  }))
}

export function formatDriveRelativeDate(iso?: string, nowMs: number = Date.now()): string {
  return formatRelativeTime(iso, nowMs)
}

export function isDriveFolder(mimeType: string): boolean {
  return mimeType === 'application/vnd.google-apps.folder'
}

export const SHARED_DRIVES_CRUMB_ID = '__shared_drives__'

export function uniqueDriveItemsById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const item of items) {
    if (!item.id || seen.has(item.id)) continue
    seen.add(item.id)
    out.push(item)
  }
  return out
}

export function mapBrowseResponseToTree(
  items: DriveBrowseItem[],
  opts: { importedIds: Set<string> },
): SidebarTreeNode<DriveFileMeta>[] {
  return uniqueDriveItemsById(items).map((item) => {
    const folder = item.isFolder ?? isDriveFolder(item.mimeType)
    const isShortcut = item.isShortcut ?? false
    // For shortcuts, derive the icon from the target's type (what it points to)
    const iconMime = isShortcut && item.shortcutTarget ? item.shortcutTarget.mimeType : item.mimeType
    // Shared-with-me files get a subtle subtitle indicator
    const sharedBadge = !item.ownedByMe && item.ownedByMe !== undefined ? 'Shared' : undefined
    const shortcutBadge = isShortcut ? 'Shortcut' : undefined
    return {
      id: item.id,
      kind: folder ? 'folder' : 'file',
      label: item.name,
      icon: isShortcut
        ? 'gshortcut'
        : sidebarTreeIconForMime(iconMime, folder),
      subtitle: formatDriveRelativeDate(item.modifiedTime),
      badge: !folder && opts.importedIds.has(item.id)
        ? 'In Maater'
        : (shortcutBadge ?? sharedBadge),
      meta: {
        fileId: item.id,
        mimeType: item.mimeType,
        webViewLink: item.webViewLink,
        isFolder: folder,
        isShortcut,
        ownedByMe: item.ownedByMe,
        shared: item.shared,
        shortcutTarget: item.shortcutTarget ?? null,
      },
    }
  })
}

export function mapDrivesToSections(
  drives: Array<{ id: string; name: string }>,
  itemsByDriveId: Record<string, SidebarTreeNode<DriveFileMeta>[]>,
): SidebarTreeSection<DriveFileMeta>[] {
  return drives.map((drive) => ({
    id: drive.id,
    label: drive.name,
    nodes: itemsByDriveId[drive.id] ?? [],
  }))
}

export function mapBreadcrumbsFromApi(
  crumbs: Array<{ id: string; name: string }> | undefined,
): SidebarTreeBreadcrumb[] {
  return (crumbs ?? []).map((c) => ({ id: c.id, label: c.name }))
}

/** Shared drives list + drive name once, then nested API crumbs (skip the drive root). */
export function mergeSharedDriveBreadcrumbs(opts: {
  driveId?: string
  driveName?: string
  apiCrumbs: SidebarTreeBreadcrumb[]
}): SidebarTreeBreadcrumb[] {
  const shared: SidebarTreeBreadcrumb = {
    id: SHARED_DRIVES_CRUMB_ID,
    label: 'Shared drives',
  }
  const seen = new Set<string>([shared.id])
  const out: SidebarTreeBreadcrumb[] = [shared]
  if (opts.driveId) {
    out.push({ id: opts.driveId, label: opts.driveName ?? 'Shared drive' })
    seen.add(opts.driveId)
  }
  for (const crumb of opts.apiCrumbs) {
    if (!crumb.id || seen.has(crumb.id)) continue
    seen.add(crumb.id)
    out.push(crumb)
  }
  return out
}

export function buildDriveContextMenu(
  node: SidebarTreeNode<DriveFileMeta>,
  handlers: {
    onOpen: () => void
    onImport?: () => void
    onPush?: () => void
    onReimport?: () => void
    onOpenExternal?: () => void
    imported?: boolean
    importing?: boolean
    pushing?: boolean
  },
): EntityContextMenuEntry[] {
  const entries: EntityContextMenuEntry[] = [
    {
      label: 'Open',
      icon: OpenDestinationIcons.open,
      onSelect: handlers.onOpen,
    },
  ]

  const effectiveMime = driveEffectiveMime(
    node.meta?.mimeType,
    node.meta?.shortcutTarget?.mimeType,
  )
  const canImport = isDriveFileImportable(effectiveMime)

  if (handlers.imported) {
    entries.push(
      {
        label: 'Open in Maater',
        icon: FolderOpen,
        onSelect: handlers.onOpen,
      },
      {
        label: handlers.pushing ? 'Syncing...' : 'Sync with Drive',
        icon: ArrowUpFromLine,
        disabled: handlers.pushing,
        onSelect: () => handlers.onPush?.(),
      },
      {
        label: handlers.importing ? 'Re-importing...' : 'Re-import from Drive',
        icon: RefreshCw,
        disabled: handlers.importing,
        onSelect: () => handlers.onReimport?.(),
      },
    )
  } else if (handlers.onImport && canImport) {
    entries.push({
      label: handlers.importing ? 'Importing...' : 'Import to Maater',
      icon: Download,
      disabled: handlers.importing,
      onSelect: handlers.onImport,
    })
  }

  if (handlers.onOpenExternal) {
    entries.push({
      label: 'Open in Drive',
      icon: resolveOpenExternalIcon(GoogleDriveIcon),
      onSelect: handlers.onOpenExternal,
    })
  }

  return entries
}

export function getDrivePreviewUrl(fileId: string, mimeType: string): string | null {
  if (mimeType === 'application/vnd.google-apps.document') {
    return `https://docs.google.com/document/d/${fileId}/preview`
  }
  if (mimeType === 'application/vnd.google-apps.spreadsheet') {
    return `https://docs.google.com/spreadsheets/d/${fileId}/preview`
  }
  if (mimeType === 'application/vnd.google-apps.presentation') {
    return `https://docs.google.com/presentation/d/${fileId}/preview`
  }
  if (mimeType === 'application/vnd.google-apps.form') {
    return `https://docs.google.com/forms/d/${fileId}/viewform`
  }
  if (mimeType === 'application/vnd.google-apps.drawing') {
    return `https://docs.google.com/drawings/d/${fileId}/preview`
  }
  // Shortcuts require resolution to the target before we can preview —
  // the caller should pass target fileId/mimeType instead.
  if (mimeType === 'application/vnd.google-apps.shortcut' || mimeType === '') {
    return null
  }
  // Non-native files (PDFs, Word docs, images, CSV, uploaded markdown, etc.):
  // drive.google.com/file/d/{id}/preview is blocked by Google CSP when
  // embedded in third-party origins. Return null so callers can fetch
  // /api/google-drive/files/:fileId/content for .md/.txt, or show Open in Drive.
  return null
}
